import { HostContext, useAsyncMemo, WorkItemContext } from "@buzzeasy/shared-frontend-utilities";
import { SignalRConnectionEventHandlers, SignalRHubV2 } from "@buzzeasy/shared-frontend-utilities/signalr";
import { HubConnectionState } from "@microsoft/signalr";
import { MessageInstance } from "antd/es/message/interface";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import environmentConfig from "../environmentConfig";
import { CallTranslatorHub, CallTranslatorHubClient, CallTranslatorSignalRHub } from "../model/callTranslatorHub";
import { AudioStatusDto, CallTranslatorMode, IntermediateTranscriptItem, IntermediateTranscriptStatus, SpeechStatus, SpeechTranscriptItem, TranscriptHistoryItem } from "../model/hubDtos";
import { SpeechConfirmationConfig } from "../model/speechConfirmation";
import { setCopilotAccess } from "../redux/agentCopilotAccessSlice";
import { useLazyGetCallTranslatorHubUrlQuery } from "../redux/apis/callTranslatorApi";
import { useAppDispatch } from "../redux/store";

const isInLocalDevelopment = environmentConfig.environment === "dev" && environmentConfig.buildNumber === "localDevelopment";

// set this to false if you need to test the reconnect logic in local development (otherwise keep it true so that we do not spam requests to the server)
const disableReconnectForLocalDevelopment = true;

const connectionMessageKey = "ctui-connection";
const connectRetryDelays = [
  ...Array.from({ length: 9 }, () => 1000),
  ...Array.from({ length: 11 }, () => 5000),
]; // try every second for 10 seconds, then every 5 seconds for 1 minute

const defaultSpeechTimeStamp = new Date().toISOString();

export interface TranscriptHub {
  isLoading: boolean;
  isError: boolean;
  isChatOnlyMode: boolean;
  speechConfirmation: SpeechConfirmationConfig;

  isPlayingAudioToCustomer: boolean;
  isAgentSpeaking: boolean;

  transcriptItems: TranscriptHistoryItem[];
  intermediateTranscriptItem: IntermediateTranscriptItem | null;
  spokenTranscript: SpeechTranscriptItem | null;
  sendTextMessage(text: string): void;

  volume: number;
  changeVolume(volume: number): Promise<boolean>;
  isTranslationEnabled: boolean;
  changeTranslationEnabled(enabled: boolean): Promise<boolean>;
  isCustomerAudioEnabled: boolean;
  changeCustomerAudioEnabled(enabled: boolean): Promise<boolean>;
  isChangingCustomerAudioStatusDisabled: boolean;

  mode: CallTranslatorMode;
}

export default function useTranscriptHub(conversationId: string, messageApi: MessageInstance, mode: CallTranslatorMode): TranscriptHub {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const { apiBaseUrl, token } = useContext(HostContext);
  const { wrapUp: { copilotId } } = useContext(WorkItemContext);

  const hubRef = useRef<CallTranslatorSignalRHub | null>(null);

  const [isChatOnlyMode, setIsChatOnlyMode] = useState(false);
  const [isPlayingAudioToCustomer, setIsPlayingAudioToCustomer] = useState(false);
  const [agentSpeechStatus, setAgentSpeechStatus] = useState<SpeechStatus>({ isSpeaking: false, timeStampUtc: defaultSpeechTimeStamp });
  const [transcriptItems, setTranscriptItems] = useState<TranscriptHistoryItem[]>([]);
  const [intermediateTranscriptItem, setIntermediateTranscriptItem] = useState<IntermediateTranscriptItem | null>(null);
  const [volume, setVolume] = useState(0);
  const [isTranslationEnabled, setIsTranslationEnabled] = useState(true);
  const [isCustomerIntermediateTranslationEnabled, setIsCustomerIntermediateTranslationEnabled] = useState(false);
  const [speechConfirmation, setSpeechConfirmation] = useState<SpeechConfirmationConfig>({ isEnabled: false, timeoutSeconds: 5 });
  const [spokenTranscript, setSpokenTranscript] = useState<SpeechTranscriptItem | null>(null);
  const [customerAudioStatus, setCustomerAudioStatus] = useState<AudioStatusDto>({ isAudioEnabled: true, isChangingAudioStatusDisabled: false });

  const volumeRef = useRef(volume);
  volumeRef.current = volume;

  const isIntermediateTranscriptEnabledRef = useRef(isCustomerIntermediateTranslationEnabled);
  isIntermediateTranscriptEnabledRef.current = isCustomerIntermediateTranslationEnabled;

  const intermediateTranscriptItemRef = useRef(intermediateTranscriptItem);
  intermediateTranscriptItemRef.current = intermediateTranscriptItem;

  const receiveTranscriptHistoryItems = useCallback(
    (updatedItems: TranscriptHistoryItem[]) => setTranscriptItems((previousItems) => {
      const dictionary = Object.fromEntries(previousItems.map(item => [item.id, item]));

      for (const updatedItem of updatedItems) {
        const itemExists = !!dictionary[updatedItem.id];
        const isUpdatedItemTranslated = updatedItem.translatedText !== null;

        if (isIntermediateTranscriptEnabledRef.current) {
          if (isUpdatedItemTranslated) { // only add translated items
            dictionary[updatedItem.id] = updatedItem;

            if (intermediateTranscriptItemRef.current && new Date(intermediateTranscriptItemRef.current.timeStampUtc).getTime() < new Date(updatedItem.timeStampUtc).getTime()) {
              setIntermediateTranscriptItem(null);
            }
          }
        }
        else {
          if (!itemExists || isUpdatedItemTranslated || mode === "transcription") {
            dictionary[updatedItem.id] = updatedItem;
          }
        }
      }

      const values = Object.values(dictionary);
      return values.sort((a, b) => new Date(a.timeStampUtc).getTime() - new Date(b.timeStampUtc).getTime());
    }),
    [mode],
  );

  const receiveIntermediateTranscriptStatus = useCallback(
    (data: IntermediateTranscriptStatus) => {
      if (data.participant === "Customer") {
        setIsCustomerIntermediateTranslationEnabled(data.isIntermediateTranscriptEnabled);
      }
    },
    [],
  );

  const receiveIntermediateTranscriptItem = useCallback(
    (updatedItem: IntermediateTranscriptItem) => setIntermediateTranscriptItem(updatedItem),
    [],
  );

  const receiveAgentSpeechStatus = useCallback(
    (data: SpeechStatus) => setAgentSpeechStatus(prev => new Date(data.timeStampUtc).getTime() > new Date(prev.timeStampUtc).getTime()
      ? data
      : prev,
    ),
    [],
  );

  const changeVolume = useCallback(
    async (volume: number) => {
      if (!hubRef.current || hubRef.current.state !== HubConnectionState.Connected)
        return false;

      const result = await hubRef.current.sendChangeOriginalCustomerAudioVolume({ volumePercentage: volume });

      if (result.isVolumeChanged) {
        setVolume(volume);
        return true;
      }
      else {
        return false;
      }
    },
    [],
  );

  const changeTranslationEnabled = useCallback(
    async (enabled: boolean) => {
      if (!hubRef.current || hubRef.current.state !== HubConnectionState.Connected)
        return false;

      const result = await hubRef.current?.sendChangeTranslationStatus({ isTranslationEnabled: enabled });
      return result.isTranslationStatusUpdated;
    },
    [],
  );

  const changeCustomerAudioEnabled = useCallback(
    async (enabled: boolean) => {
      if (!hubRef.current || hubRef.current.state !== HubConnectionState.Connected)
        return false;

      const result = await hubRef.current?.sendChangeTranslatedCustomerAudioStatus({ isAudioEnabled: enabled });

      if (result.isAudioStatusUpdated) {
        setCustomerAudioStatus(prev => ({ ...prev, isAudioEnabled: enabled }));
        return true;
      }
      else {
        return false;
      }
    },
    [],
  );

  const sendTextMessage = useCallback(
    (text: string) => {
      if (!hubRef.current || hubRef.current.state !== HubConnectionState.Connected)
        return;

      void hubRef.current.sendPlayTextMessageToCustomer({ text });
    },
    [],
  );

  const connectionEventHandlers = useMemo<Partial<SignalRConnectionEventHandlers>>(
    () => ({
      onConnectionLost: () => void messageApi.warning({ key: connectionMessageKey, content: t("notifications.connectionLost"), duration: 0 }),
      onConnectionRestored: () => void messageApi.success({ key: connectionMessageKey, content: t("notifications.connectionRestored") }),
      onConnectionRestoreFailed: () => void messageApi.error({ key: connectionMessageKey, content: t("notifications.connectionRestoreFailed") }),
    }),
    [messageApi, t],
  );

  const [getCallTranslatorUrl] = useLazyGetCallTranslatorHubUrlQuery();

  const hubUrl = useAsyncMemo<string | null>(
    async () => {
      if (mode === "transcription") {
        return `${apiBaseUrl}/calltranscriber/hub?conversationId=${conversationId}`;
      }

      let retryIndex = (isInLocalDevelopment && disableReconnectForLocalDevelopment)
        ? connectRetryDelays.length - 1 // limit to a single attempt
        : 0;

      while (retryIndex < connectRetryDelays.length) {
        try {
          const url = await getCallTranslatorUrl(conversationId, false).unwrap();

          if (url)
            return url;
        }
        catch { /* do nothing */ }

        const delay = connectRetryDelays[retryIndex];
        retryIndex++;
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      return null;
    },
    [conversationId, mode, apiBaseUrl, getCallTranslatorUrl],
  );

  useEffect(
    () => {
      if (copilotId) {
        dispatch(setCopilotAccess({ copilotId, accessToken: token }));
      }
    },
    [copilotId, token, dispatch],
  );

  useEffect(
    () => {
      if (!hubUrl)
        return;

      const hub = SignalRHubV2.create<CallTranslatorHub, CallTranslatorHubClient>(hubUrl, token, connectionEventHandlers);
      hubRef.current = hub;

      hub.on("ReceiveTranscriptHistoryItems", receiveTranscriptHistoryItems);

      if (mode === "translation") {
        hub.on("ReceiveOriginalCustomerAudioVolume", data => setVolume(data.volumePercentage));
        hub.on("ReceiveAudioContentPlayedToCustomer", data => setIsPlayingAudioToCustomer(data.audioContentType === "TranslatedSpeech"));
        hub.on("ReceiveAgentWorkingMode", data => setIsChatOnlyMode(data.isChatMode));
        hub.on("ReceiveTranslationStatus", data => setIsTranslationEnabled(data.isTranslationEnabled));
        hub.on("ReceiveIntermediateTranscriptStatus", receiveIntermediateTranscriptStatus);
        hub.on("ReceiveIntermediateTranscriptItem", receiveIntermediateTranscriptItem);
        hub.on("ReceiveAgentCopilotAccess", data => dispatch(setCopilotAccess(data)));
        hub.on("ReceiveAgentTranscriptConfirmationStatus", data => setSpeechConfirmation({ isEnabled: data.isTranscriptConfirmationEnabled, timeoutSeconds: data.transcriptConfirmationTimeoutSeconds }));
        hub.on("ReceiveAgentTranscriptItemForConfirmation", setSpokenTranscript);
        hub.on("ReceiveAgentSpeechStatus", receiveAgentSpeechStatus);
        hub.on("ReceiveTranslatedCustomerAudioStatus", setCustomerAudioStatus);
      }

      void hub.start();
      return () => void hub.stop();
    },
    [hubUrl, mode, token, connectionEventHandlers, dispatch, receiveAgentSpeechStatus, receiveIntermediateTranscriptItem, receiveIntermediateTranscriptStatus, receiveTranscriptHistoryItems],
  );

  return useMemo<TranscriptHub>(
    () => ({
      isLoading: hubUrl === undefined,
      isError: hubUrl === null,
      isChatOnlyMode,
      speechConfirmation,

      isPlayingAudioToCustomer,
      isAgentSpeaking: agentSpeechStatus.isSpeaking,

      transcriptItems,
      intermediateTranscriptItem,
      spokenTranscript,
      sendTextMessage,

      volume,
      changeVolume,
      isTranslationEnabled,
      changeTranslationEnabled,
      isCustomerAudioEnabled: customerAudioStatus.isAudioEnabled,
      changeCustomerAudioEnabled,
      isChangingCustomerAudioStatusDisabled: customerAudioStatus.isChangingAudioStatusDisabled,

      mode,
    }),
    [hubUrl, isChatOnlyMode, speechConfirmation, isPlayingAudioToCustomer, agentSpeechStatus.isSpeaking, transcriptItems, intermediateTranscriptItem, spokenTranscript, sendTextMessage, volume, changeVolume, isTranslationEnabled, changeTranslationEnabled, customerAudioStatus, changeCustomerAudioEnabled, mode],
  );
}
